package com.pincer.app.viewmodel

import kotlin.js.JsName

/*
A richer way to show a node, for renderers that can: a grid of suggested times, a date picker. It's only ever a
decoration. The plain version is always there too, so a renderer that doesn't know the type, a screen reader and an
agent lose nothing:

- on a control, the plain version is the control itself (a date picker is a better way to fill in an input that
  takes the same text)
- on any other node, it's the node's children: the same information as text, and the same actions as plain controls

Rich descriptions carry data, not wording: the wording is in the plain version, translated and ready to read.
NodeTests and the scripted sessions check that every rich node in a real tree has its plain version.

A gallery (event_space) and a map (calendars_and_external_data) wait for the data they would show.
See docs/design_notes/abstract_client.md.
*/
sealed interface Rich {

    @JsName("type")
    fun type(): String

    /*
    Suggested times, for drawing as days by hours. The node's children are the same suggestions as text, each with its
    button to choose it; `key` names that child. Times are local to `zone`, the pincer's time zone, as the candidate
    search works in it (the plain version shows them in the person's own zone and language).
    */
    data class AvailabilityGrid(
        @JsName("zone")  val zone:  String,
                         val slots: List<GridSlot>,
    ): Rich {
        override fun type() = "AvailabilityGrid"
        @JsName("eachSlot") fun eachSlot(f: (GridSlot) -> Unit) = slots.forEach(f)
    }

    // For an input that takes a date (yyyy-mm-dd), a time (hh:mm) or both (yyyy-mm-ddThh:mm), as HTML's inputs do
    data class DateTimePicker(
                         val format: PickerFormat,
    ): Rich {
        override fun type() = "DateTimePicker"
        @JsName("formatName") fun formatName() = format.name
    }

}

data class GridSlot(
    @JsName("key")       val key:       String,  // the child node with the plain version
    @JsName("start")     val start:     String,  // ISO local date-time in the grid's zone
    @JsName("minutes")   val minutes:   Int,
    @JsName("available") val available: Int,     // how many people can make it
    @JsName("undecided") val undecided: Int,     // how many haven't said
)

enum class PickerFormat { DATE, TIME, DATETIME }

// The check NodeTests and the scripted sessions run over real trees: no rich node without a plain version
fun richWithoutPlainVersion(root: Node): List<String> {
    val missing = mutableListOf<String>()
    root.walk { node ->
        val rich = node.rich ?: return@walk
        val plain = when (rich) {
            is Rich.DateTimePicker   -> node.controlType == ControlType.Input
            is Rich.AvailabilityGrid -> rich.slots.all { slot -> node.child(slot.key)?.let { it.hasChildren() } ?: false }
        }
        if (!plain) missing.add(node.compoundKey())
    }
    return missing
}
